// Buscador ⌘K: navega a cualquier módulo/pestaña que el usuario PUEDE abrir
// (mismo gating que el resto) y a las acciones del shell. Teclado: ↑ ↓ Enter Esc.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Search } from 'lucide-react';

import { resolveItemLabel, resolveModule } from '@/modules';
import { SHELL_NAV, canOpen, resolveTarget, toPath, type Access } from './nav';

interface Entry {
  id: string;
  label: string;
  hint: string;
  path: string;
}

function norm(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function CommandPalette({
  open,
  onClose,
  access,
  archetype,
}: {
  open: boolean;
  onClose: () => void;
  access: Access;
  archetype: string | null;
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const prevFocus = useRef<HTMLElement | null>(null);

  const entries = useMemo<Entry[]>(() => {
    const out: Entry[] = [
      { id: 'new-chat', label: 'Nuevo chat con el asistente', hint: 'Acción', path: `/m/orion/asistente?nuevo=1` },
    ];
    if (canOpen('orion/agentes', access)) {
      out.push({ id: 'new-agent', label: 'Crear nuevo agente', hint: 'Acción', path: '/m/orion/agentes?nuevo=1' });
    }
    for (const item of SHELL_NAV) {
      const t = resolveTarget(item, access);
      if (t) out.push({ id: `nav-${item.key}`, label: item.label, hint: 'Menú', path: toPath(t) });
    }
    for (const m of access.availableModules) {
      const name = resolveModule(m, archetype).name;
      for (const it of m.sidebarItems) {
        if (!canOpen(`${m.key}/${it.tab}`, access)) continue;
        out.push({
          id: `${m.key}/${it.tab}`,
          label: resolveItemLabel(m, it, archetype),
          hint: name,
          path: `/m/${m.key}/${it.tab}`,
        });
      }
    }
    return out;
  }, [access, archetype]);

  const results = useMemo(() => {
    const n = norm(q.trim());
    if (!n) return entries.slice(0, 40);
    return entries.filter((e) => norm(`${e.label} ${e.hint}`).includes(n)).slice(0, 40);
  }, [entries, q]);

  useEffect(() => {
    if (open) {
      prevFocus.current = document.activeElement as HTMLElement | null;
      setQ('');
      setIdx(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      prevFocus.current?.focus?.();
    }
  }, [open]);

  useEffect(() => {
    setIdx(0);
  }, [q]);

  if (!open) return null;

  const choose = (e: Entry | undefined) => {
    if (!e) return;
    onClose();
    navigate(e.id === 'new-chat' ? `/m/orion/asistente?nuevo=${Date.now()}` : e.path);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(results[idx]);
    } else if (e.key === 'Tab') {
      // Foco atrapado en el diálogo (solo tiene el campo de búsqueda).
      e.preventDefault();
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-start justify-center px-3 pt-[12vh]" onKeyDown={onKey}>
      <div className="absolute inset-0 bg-black/40" aria-hidden onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Buscar"
        className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-border bg-popover text-popover-foreground shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-border px-4">
          <Search size={18} aria-hidden className="text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="¿A dónde quieres ir?"
            aria-label="Buscar módulos y acciones"
            role="combobox"
            aria-expanded="true"
            aria-controls="orion-palette-list"
            aria-activedescendant={results[idx] ? `pal-${idx}` : undefined}
            className="h-14 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground"
          />
          <kbd className="rounded-md border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">Esc</kbd>
        </div>
        <ul id="orion-palette-list" role="listbox" className="max-h-[50vh] overflow-y-auto p-1.5">
          {results.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">Sin resultados para «{q}».</li>
          )}
          {results.map((e, i) => (
            <li
              key={e.id}
              id={`pal-${i}`}
              role="option"
              aria-selected={i === idx}
              onMouseEnter={() => setIdx(i)}
              onClick={() => choose(e)}
              className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm ${
                i === idx ? 'bg-muted' : ''
              }`}
            >
              <span className="min-w-0 flex-1 truncate font-medium">{e.label}</span>
              <span className="shrink-0 truncate text-xs text-muted-foreground">{e.hint}</span>
              {i === idx && <CornerDownLeft size={14} aria-hidden className="shrink-0 text-muted-foreground" />}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
